// File Location: src/components/policy/PolicyApplicationDetails.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  User,
  FileText,
  MessageSquare,
  Activity,
  Shield,
  Eye,
  Download,
  X,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Building,
  Stethoscope,
  CheckCircle,
  AlertTriangle,
  Award,
  Edit,
  Save,
  CreditCard,
  Paperclip,
} from "lucide-react";
import { getApplicationForPolicyReview } from "../../services/policyWriterService";
import { getApplicationSanctions, updatePolicyInformation } from "../../services/applicationService";
import DiscussionComponent from "../../component/application/DiscussionComponent";
import ConsultationScheduler from "../../component/policy/ConsultationScheduler";
import PolicyVerificationForm from "./PolicyVerificationForm";
import ApplicationRightPanel from "../../components/policy/ApplicationRightPanel";
import DisputeManager from "../../components/common/DisputeManager";
import { checkApplicationDisputeStatus, getApplicationDisputes } from "../../services/disputeService";
import { 
  raiseDocumentQuery, 
  getDocumentQueries 
} from "../../services/documentQueryService";
import { useAuth } from "../../context/AuthContext";
import { auth } from "../../services/firebaseConfig";
import SanctionLettersSection from '../../components/common/SanctionLettersSection';
import GlobalSearchBar from '../../components/common/GlobalSearchBar';

const PolicyApplicationDetails = ({ 
  applicationId: propApplicationId, 
  initialActiveTab = "details", 
  onBack 
}) => {
  const { applicationId: paramApplicationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, roles, activeRole, setActiveRole } = useAuth();
  const user = { ...userData, uid: auth?.currentUser?.uid } || {};

  // Ensure correct role when landing directly on this page in a new tab
  useEffect(() => {
    try {
      if (Array.isArray(roles) && roles.includes('PolicyWriter') && activeRole !== 'PolicyWriter') {
        setActiveRole('PolicyWriter');
      }
    } catch (_) {}
  }, [roles, activeRole, setActiveRole]);

  // Use prop applicationId if provided, otherwise use URL param
  const applicationId = propApplicationId || paramApplicationId;

  const initialTabFromQuery = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const t = params.get('tab');
      return t || initialActiveTab;
    } catch (_) {
      return initialActiveTab;
    }
  })();
  const [activeTab, setActiveTab] = useState(initialTabFromQuery);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [timelineEvents, setTimelineEvents] = useState([]);
  // eNACH state
  const [enachForm, setEnachForm] = useState({ link: '', comments: '' });
  const [enachFile, setEnachFile] = useState(null);
  const [enachBusy, setEnachBusy] = useState(false);
  const [enachMsg, setEnachMsg] = useState('');
  const [enachErr, setEnachErr] = useState('');
  
  // Document viewer state - changed from modal to side panel
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Sanctions and Letters state
  const [sanctions, setSanctions] = useState([]);
  
  // Auto-save state for policy review
  const [autoSaveData, setAutoSaveData] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [savedDataAvailable, setSavedDataAvailable] = useState(false);
  const [isFormInViewMode, setIsFormInViewMode] = useState(false);
  const [snapshotEditing, setSnapshotEditing] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotForm, setSnapshotForm] = useState({
    policyNumber: '',
    insuranceCompany: '',
    policyType: '',
    tpaName: ''
  });

  // Robust download helper (forces actual download via Blob)
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const contentType = response.headers.get("content-type") || "";
      const blob = await response.blob();
      let finalName = filename || "document";
      if (!/\.[a-zA-Z0-9]{2,5}$/.test(finalName)) {
        const extMap = {
          "application/pdf": ".pdf",
          "image/jpeg": ".jpg",
          "image/jpg": ".jpg",
          "image/png": ".png",
          "image/webp": ".webp",
        };
        const ext = extMap[contentType];
        if (ext) finalName += ext;
      }
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed, opening in new tab:", err);
      window.open(url, "_blank");
    }
  };

  // Initialize snapshot form when application loads or editing toggles on
  useEffect(() => {
    if (!application) return;
    setSnapshotForm({
      policyNumber: application.policy?.number || '',
      insuranceCompany: application.policy?.insuranceCompany || '',
      policyType: application.policy?.type || '',
      tpaName: application.policy?.tpaName || application.policy?.tpa || ''
    });
  }, [application]);
  
  // Collapsible sections state - policy and documents expanded by default
  const [collapsedSections, setCollapsedSections] = useState({
    patient: true,
    medical: true,
    policy: false,
    hospital: true,
    financial: true,
    documents: false,
    dischargeDocs: true // added for Discharge Documents section (collapsed by default)
  });
  
  // Discussion panel state
  const [showDiscussionPanel, setShowDiscussionPanel] = useState(false);
  
  // Clarification tracking state
  const [hasPendingClarification, setHasPendingClarification] = useState(false);
  const [clarificationDetails, setClarificationDetails] = useState(null);

  // Document query state
  const [documentQueries, setDocumentQueries] = useState({});
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryDocumentKey, setQueryDocumentKey] = useState("");
  const [queryComment, setQueryComment] = useState("");

  // Query management state
  const [disputeStatus, setDisputeStatus] = useState({
    hasActiveDisputes: false,
    activeDisputes: [],
    canProceed: true,
  });

  const isKypAccepted = () => {
    const mv = application?.workflow?.medicalVerification || {};
    const ack = mv?.kypHospitalAcknowledgement?.status || mv?.kypAck || mv?.kypStatus;
    return ack === 'accepted';
  };

  const isDispatchApproved = () => {
    const ds = application?.workflow?.dischargeSettlement || {};
    const dispatchObj = application?.workflow?.dispatch || application?.workflow?.dispatches || {};
    return (
      application?.workflow?.dispatchClearance?.status === 'approved' ||
      dispatchObj?.status === 'completed' ||
      ds?.dispatchClearance?.status === 'approved' ||
      ds?.dispatchReview?.status === 'approved'
    );
  };

  const tabs = [
    { id: "details", label: "Application Details", icon: User, count: null },
    { id: "documents", label: "Documents", icon: FileText, count: null },
    { id: "policy-review", label: "Policy Review", icon: Shield, count: null },
    { id: "enach", label: "eNACH", icon: CreditCard, count: null },
    ...(isKypAccepted() || isDispatchApproved() ? [{ id: "consultations", label: "Patient Consultations", icon: Phone, count: null }] : []),
    {
      id: "disputes",
      label: "Query",
      icon: AlertTriangle,
      count: disputeStatus.activeDisputes.length,
      alert: disputeStatus.hasActiveDisputes
    },
    {
      id: "discussions",
      label: "Discussions",
      icon: MessageSquare,
      count: unreadCount,
    },
    { id: "sanctions", label: "Letters & Documents", icon: Award, count: null },
    { id: "status", label: "Status Timeline", icon: Activity, count: null },
  ];

  // File Location: src/components/policy/PolicyApplicationDetails.jsx (Continued from previous part)

  useEffect(() => {
    if (applicationId) {
      loadApplicationData();
      loadDocumentQueries();
      // Load any saved policy review data
      const savedData = loadFromLocalStorage();
      if (savedData) {
        console.log('Loaded saved policy review data:', savedData);
        setSavedDataAvailable(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  // Confirm on refresh/close and back navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ""; // triggers native confirm
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    let isNavigatingBack = false;
    try { window.history.pushState(null, '', window.location.href); } catch (_) {}

    const handlePopState = () => {
      if (isNavigatingBack) return;
      const confirmed = window.confirm('Are you sure you want to leave this page? Unsaved changes may be lost.');
      if (confirmed) {
        isNavigatingBack = true;
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        navigate(-2);
      } else {
        try { window.history.pushState(null, '', window.location.href); } catch (_) {}
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  const loadApplicationData = async () => {
    setLoading(true);
    try {
      const result = await getApplicationForPolicyReview(applicationId);
      if (result.success) {
        setApplication(result.application);
        try {
          await loadUnifiedTimeline(result.application);
        } catch (_) {}
        
        // Check if application is already approved (base)
        const policyStatus = result.application.workflow?.policyVerification?.status;
        const isApprovedBase = policyStatus === "approved" || 
                          result.application.status === 'approved' ||
                          result.application.policy?.verificationStatus === 'approved';

        // Override: if KYP challenged and reassigned to Policy, enable editing again
        const isChallengedForPolicy = (
          result.application?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
          (
            result.application?.workflow?.medicalVerification?.kypReassignment?.target === 'policy' ||
            (result.application?.workflow?.medicalVerification?.kypReassignment?.targets || [])?.includes?.('policy')
          )
        );

        setIsFormInViewMode(!(isChallengedForPolicy) && isApprovedBase);
        
        // Check for pending clarifications
        const hasPending = policyStatus === "pending_info" || policyStatus === "pending_policy_clarification";
        setHasPendingClarification(hasPending);
        
        if (hasPending && result.application.workflow?.policyVerification?.clarificationRequestId) {
          setClarificationDetails({
            requestId: result.application.workflow.policyVerification.clarificationRequestId,
            status: policyStatus
          });
        }
        
        // Set default tab based on policy verification status (unless URL enforces a tab)
        if (!new URLSearchParams(location.search).get('tab')) {
          if (policyStatus === "pending" || !policyStatus) {
            setActiveTab("policy-review");
          }
        }
        
  // If approved, load the approved data instead of draft
  if (isApprovedBase && result.application.workflow?.policyVerification?.verificationData) {
          setAutoSaveData({
            verificationData: result.application.workflow.policyVerification.verificationData,
            lastModified: result.application.workflow.policyVerification.verifiedAt,
            isApproved: true
          });
        }
      } else {
        console.error("Error loading application:", result.error);
      }
    } catch (error) {
      console.error("Error loading application:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnifiedTimeline = async (app) => {
    const safeToDate = (ts) => {
      if (!ts) return null;
      try { return ts?.toDate ? ts.toDate() : new Date(ts); } catch (_) { return null; }
    };
    const events = [];

    if (app?.submittedAt) {
      events.push({ id: 'submission', type: 'submission', title: 'Application Submitted', description: 'Hospital submitted the patient application', timestamp: safeToDate(app.submittedAt), completed: true });
    }

    const wf = app?.workflow || {};
    const map = [
      { key: 'medicalVerification', type: 'medical_review', title: 'Medical Review' },
      { key: 'policyVerification', type: 'policy_review', title: 'Policy Verification' },
      { key: 'financialVerification', type: 'financial_review', title: 'Financial Verification' },
      { key: 'kycVerification', type: 'kyc_verification', title: 'KYC Verification' },
    ];
    map.forEach(({ key, type, title }) => {
      const node = wf[key];
      if (!node) return;
      const ts = node.verifiedAt || node.updatedAt || node.approvedAt;
      if (!ts) return;
      events.push({ id: `${type}`, type, title, description: String(node.status || '').replace('_',' '), timestamp: safeToDate(ts), completed: node.status === 'approved', actor: node.verifiedBy ? { name: node.verifiedBy, team: type.includes('policy') ? 'policy' : type.includes('medical') ? 'medical' : type.includes('financial') ? 'financial' : undefined } : undefined });
    });

    if (app?.statusHistory && typeof app.statusHistory === 'object') {
      Object.entries(app.statusHistory).forEach(([status, data]) => {
        events.push({ id: `status-${status}`, type: 'status_change', title: `Status changed to ${status.replace('_',' ').toUpperCase()}`, description: data?.comments || (data?.updatedBy ? `Updated by ${data.updatedBy}` : ''), timestamp: safeToDate(data?.updatedAt), actor: data?.updatedBy ? { name: data.updatedBy } : undefined, completed: true });
      });
    }

    const [disputesRes, docQueriesRes] = await Promise.all([
      getApplicationDisputes(app.applicationId),
      getDocumentQueries(app.applicationId),
    ]);

    if (disputesRes?.success) {
      disputesRes.disputes.forEach((d) => {
        if (d.raisedAt) events.push({ id: `dispute-${d.id}-raised`, type: 'dispute_raised', title: d.title || 'Query Raised', description: d.description, timestamp: safeToDate(d.raisedAt), actor: d.raisedBy ? { name: d.raisedBy.userName, team: d.raisedBy.department } : undefined, completed: false });
        if (d.acknowledgedAt) events.push({ id: `dispute-${d.id}-ack`, type: 'acknowledged', title: 'Query Acknowledged', description: '', timestamp: safeToDate(d.acknowledgedAt), completed: false });
        if (d.resolvedAt) events.push({ id: `dispute-${d.id}-resolved`, type: 'dispute_resolved', title: 'Query Resolved', description: d.resolution || 'Resolution recorded', timestamp: safeToDate(d.resolvedAt), actor: d.resolvedBy ? { name: d.resolvedBy.userName, team: d.resolvedBy.department } : undefined, attachments: Array.isArray(d.resolverDocuments) ? d.resolverDocuments.map(f => ({ name: f?.name || 'Document', url: f?.url })).filter(a => a.url) : [], completed: true });
      });
    }

    if (docQueriesRes?.success) {
      docQueriesRes.queries.forEach((q) => {
        if (q.raisedAt) events.push({ id: `docq-${q.id}-open`, type: 'doc_query_open', title: `Document Query: ${q.documentTitle || q.documentKey}`, description: q.comment, timestamp: safeToDate(q.raisedAt), actor: q.raisedBy ? { name: q.raisedBy.name, team: q.raisedBy.team } : undefined, completed: q.status === 'resolved' });
        if (q.status === 'resolved' && q.resolvedAt) events.push({ id: `docq-${q.id}-resolved`, type: 'doc_query_resolved', title: `Document Updated: ${q.documentTitle || q.documentKey}`, description: 'Query resolved with updated document', timestamp: safeToDate(q.resolvedAt), actor: q.resolvedBy ? { name: q.resolvedBy.name, team: 'hospital' } : undefined, attachments: q.updatedDocumentUrl ? [{ name: q.updatedDocumentFileName || 'Updated Document', url: q.updatedDocumentUrl }] : [], completed: true });
      });
    }

    events.sort((a,b) => (b.timestamp ? new Date(b.timestamp).getTime() : 0) - (a.timestamp ? new Date(a.timestamp).getTime() : 0));
    setTimelineEvents(events);
  };

  const checkDisputeStatus = async () => {
    try {
      // Only block Policy actions if a policy-raised query is active
      const result = await checkApplicationDisputeStatus(applicationId, { department: 'policy' });
      if (result.success) {
        setDisputeStatus({
          hasActiveDisputes: result.hasActiveDisputes,
          activeDisputes: result.activeDisputes,
          canProceed: result.canProceed,
        });
      }
    } catch (error) {
      console.error('Error checking dispute status:', error);
    }
  };

  // Load dispute status when component mounts or applicationId changes
  useEffect(() => {
    if (applicationId) {
      checkDisputeStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const loadSanctions = useCallback(async () => {
    try {
      const result = await getApplicationSanctions(applicationId);
      if (result.success) {
        setSanctions(result.sanctions);
      }
    } catch (error) {
      console.error('Error loading sanctions:', error);
    }
  }, [applicationId]);

  useEffect(() => {
    if (activeTab === 'sanctions') {
      loadSanctions();
    }
  }, [activeTab, loadSanctions]);

  const handleDisputeUpdate = () => {
    checkDisputeStatus();
  };

  const loadDocumentQueries = async () => {
    try {
      const queries = await getDocumentQueries(applicationId);
      const queriesByDoc = {};
      queries.forEach(query => {
        if (!queriesByDoc[query.documentKey]) {
          queriesByDoc[query.documentKey] = [];
        }
        queriesByDoc[query.documentKey].push(query);
      });
      setDocumentQueries(queriesByDoc);
    } catch (error) {
      console.error("Error loading document queries:", error);
    }
  };

  const openQueryModal = (documentKey) => {
    setQueryDocumentKey(documentKey);
    setShowQueryModal(true);
  };

  const handleRaiseQuery = async () => {
    if (!queryComment.trim()) {
      alert("Please enter a comment");
      return;
    }

    try {
      await raiseDocumentQuery(
        applicationId,
        queryDocumentKey,
        queryComment,
        {
          uid: user.uid,
          name: user.displayName || user.email,
          email: user.email,
          team: 'policy'
        }
      );

      await loadDocumentQueries();
      setShowQueryModal(false);
      setQueryComment("");
      setQueryDocumentKey("");
      alert("Query raised successfully");
    } catch (error) {
      console.error("Error raising query:", error);
      alert("Failed to raise query");
    }
  };

  const handleUnreadCountChange = (count) => {
    setUnreadCount(count);
  };

  const handlePolicyVerificationSubmit = (result) => {
    // Refresh application data after policy verification
    loadApplicationData();
    // Clear auto-saved data on successful submission
    localStorage.removeItem(`policy_review_${applicationId}`);
    setAutoSaveData(null);
    setLastSavedAt(null);
    setSavedDataAvailable(false);
    setIsFormInViewMode(true);
  };

  // Auto-save functionality
  const saveToLocalStorage = (data) => {
    try {
      // Don't save if form is in view mode (approved)
      if (isFormInViewMode) {
        console.log('Form is in view mode, skipping auto-save');
        return;
      }
      
      localStorage.setItem(`policy_review_${applicationId}`, JSON.stringify({
        ...data,
        savedAt: new Date().toISOString(),
        userId: user.uid,
        isApproved: false
      }));
      setAutoSaveData(data);
      setLastSavedAt(new Date());
      setSavedDataAvailable(true);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem(`policy_review_${applicationId}`);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.userId === user.uid) {
          // Only load if not approved and data exists
          if (!data.isApproved) {
            setAutoSaveData(data);
            setLastSavedAt(new Date(data.savedAt));
            setSavedDataAvailable(true);
            return data;
          }
        }
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
    return null;
  };

  const handleRequestInfo = async (clarificationData) => {
    // Save current form data before requesting info
    if (autoSaveData) {
      await saveToLocalStorage(autoSaveData);
    }
    
    // Show discussion panel on the right side
    setShowDiscussionPanel(true);
    
    // Send initial clarification message only if there's valid data
    if (clarificationData && clarificationData.message && clarificationData.subject) {
      // Wait a bit for the discussion component to load
      setTimeout(async () => {
        try {
          const messageData = {
            message: `📋 **Policy Clarification Request**\n\n**Subject:** ${clarificationData.subject}\n\n**Details:**\n${clarificationData.message}${clarificationData.requestedDocuments && clarificationData.requestedDocuments.length > 0 ? `\n\n**Requested Documents:**\n${clarificationData.requestedDocuments.map(doc => `• ${doc}`).join('\n')}` : ''}\n\n**Priority:** ${clarificationData.priority || 'Normal'}\n\n---\n*Please provide the requested information to proceed with the policy verification.*`,
            sender: user.uid,
            senderName: user.name || user.email || 'Policy Writer',
            senderRole: user.role || 'policy_writer',
            messageType: 'text',
            readBy: [user.uid],
            priority: clarificationData.priority || 'normal',
            isSystemMessage: false,
            clarificationRequest: true,
            timestamp: new Date()
          };
          
          // Import the service function
          const { sendDiscussionMessage } = await import('../../services/discussionService');
          
          const result = await sendDiscussionMessage(applicationId, messageData);
          
          if (result.success) {
            console.log('Clarification message sent successfully');
            // Update pending clarification status
            setHasPendingClarification(true);
          } else {
            console.error('Failed to send clarification message:', result.error);
          }
        } catch (error) {
          console.error('Error sending clarification message:', error);
        }
      }, 500);
    }
  };

  const openDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentPanel(true);
  };

  const closeDocumentPanel = () => {
    setShowDocumentPanel(false);
    setSelectedDocument(null);
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return "₹0";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toLocaleString("en-IN")}`;
  };



  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "submitted":
      case "medical_approved":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "policy_review_pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "policy_approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "policy_rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending_policy_clarification":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const renderApplicationDetails = () => (
    <div className="space-y-6">
      {/* Patient Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-teal-100 p-2 rounded-lg">
            <User className="h-5 w-5 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Patient Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Patient Name
            </label>
            <p className="text-lg font-semibold text-gray-900">
              {application.patient?.name}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Age & Gender
            </label>
            <p className="text-gray-900">
              {application.patient?.age} years, {application.patient?.gender}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Contact</label>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Phone className="h-3 w-3 text-gray-400" />
                <p className="text-sm text-gray-900">
                  {application.patient?.phone}
                </p>
              </div>
              {application.patient?.email && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {application.patient?.email}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Aadhar Number
            </label>
            <p className="text-gray-900 font-mono">
              ****-****-{application.patient?.kyc?.aadharNumber?.slice(-4)}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">Address</label>
            <div className="flex items-start space-x-2">
              <MapPin className="h-3 w-3 text-gray-400 mt-1" />
              <p className="text-sm text-gray-900">
                {application.patient?.address}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Medical Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Stethoscope className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Medical Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Diagnosis
              </label>
              <p className="text-gray-900 font-medium">
                {application.medical?.diagnosis}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Treatment Type
              </label>
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  application.medical?.treatmentType === "surgical"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {application.medical?.treatmentType?.charAt(0).toUpperCase() +
                  application.medical?.treatmentType?.slice(1)}
              </span>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Consulting Doctor
              </label>
              <p className="text-gray-900">
                {application.medical?.doctor?.name}
              </p>
              <p className="text-sm text-gray-600">
                {application.medical?.doctor?.designation}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">
                Procedure Details
              </label>
              <p className="text-gray-900">
                {application.medical?.procedureDetails}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Admission Date
                </label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {application.medical?.admissionDate}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Discharge Date
                </label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {application.medical?.dischargeDate || "TBD"}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Estimated Cost
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatAmount(application.medical?.estimatedCost)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Actual Cost
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {application.medical?.actualCost
                    ? formatAmount(application.medical.actualCost)
                    : "TBD"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Policy Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-green-100 p-2 rounded-lg">
            <Shield className="h-5 w-5 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Insurance Policy
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Policy Number
            </label>
            <p className="text-gray-900 font-mono">
              {application.policy?.number}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Insurance Company
            </label>
            <p className="text-gray-900">
              {application.policy?.insuranceCompany}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Policy Type
            </label>
            <span
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                application.policy?.type === "corporate"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {application.policy?.type?.charAt(0).toUpperCase() +
                application.policy?.type?.slice(1)}
            </span>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Coverage Amount
            </label>
            <p className="text-lg font-semibold text-gray-900">
              {formatAmount(application.policy?.coverageAmount)}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Policy Period
            </label>
            <p className="text-gray-900">
              {application.policy?.startDate} to {application.policy?.endDate}
            </p>
          </div>

          {application.policy?.tpaName && (
            <div>
              <label className="text-sm font-medium text-gray-500">TPA</label>
              <p className="text-gray-900">{application.policy.tpaName}</p>
            </div>
          )}
        </div>

        {/* Policy Verification Status */}
        {application.workflow?.policyVerification && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Policy Verification Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Status
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                    application.workflow.policyVerification.status
                  )}`}
                >
                  {application.workflow.policyVerification.status
                    ?.replace("_", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>

              {application.workflow.policyVerification.verifiedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Verified At
                  </label>
                  <p className="text-sm text-gray-900">
                    {formatDate(
                      application.workflow.policyVerification.verifiedAt
                    )}
                  </p>
                </div>
              )}

              {application.workflow.policyVerification
                .approvedCoverageAmount && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Approved Amount
                  </label>
                  <p className="text-lg font-semibold text-green-600">
                    {formatAmount(
                      application.workflow.policyVerification
                        .approvedCoverageAmount
                    )}
                  </p>
                </div>
              )}
            </div>

            {application.workflow.policyVerification.comments && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500">
                  Comments
                </label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3 mt-1">
                  {application.workflow.policyVerification.comments}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hospital Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Building className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Hospital Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">
              Hospital Name
            </label>
            <p className="text-gray-900 font-medium">
              {application.hospitalName}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-500">
              Submitted At
            </label>
            <p className="text-gray-900">
              {formatDate(application.submittedAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => {
    const docs = application.documents || {};

    // Grouping rules (heuristic, based on key names)
    const groups = {
      policy: [],
      patientKyc: [],
      holderKyc: [],
      medical: [],
      investigations: [],
      others: []
    };

    Object.entries(docs).forEach(([key, doc]) => {
      const k = (key || '').toLowerCase();
      if (k.includes('policy') || k.includes('ecard') || k.includes('card')) {
        groups.policy.push([key, doc]);
      } else if (k.includes('patient') && (k.includes('aadhar') || k.includes('adhar') || k.includes('pan') || k.includes('address') || k.includes('kyc'))) {
        groups.patientKyc.push([key, doc]);
      } else if ((k.includes('policyholder') || k.includes('holder')) && (k.includes('aadhar') || k.includes('adhar') || k.includes('pan') || k.includes('address') || k.includes('kyc'))) {
        groups.holderKyc.push([key, doc]);
      } else if (k.includes('medical') || k.includes('treatment') || k.includes('discharge') || k.includes('prescription')) {
        groups.medical.push([key, doc]);
      } else if (k.includes('investigation') || k.includes('report') || k.includes('lab') || k.includes('x-ray') || k.includes('xray')) {
        groups.investigations.push([key, doc]);
      } else {
        groups.others.push([key, doc]);
      }
    });

    const sections = [
      { key: 'policy', title: 'Policy Documents' },
      { key: 'patientKyc', title: 'Patient KYC' },
      { key: 'holderKyc', title: 'Policy Holder KYC' },
      { key: 'medical', title: 'Medical Documents' },
      { key: 'investigations', title: 'Investigation Reports' },
      { key: 'others', title: 'Other Documents' }
    ];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Application Documents</h2>
          <div className="text-sm text-gray-600">{Object.keys(docs).length} document(s) uploaded</div>
        </div>

        {Object.keys(docs).length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents</h3>
            <p className="text-gray-600">No documents have been uploaded for this application.</p>
          </div>
        ) : (
          sections.map((section) => {
            const items = groups[section.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={section.key} className="bg-white border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
                  <span className="text-xs text-gray-500">{items.length}</span>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(([key, doc]) => (
                    <div key={key} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <h4 className="font-medium text-gray-900 text-sm">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button onClick={() => openDocument(doc)} className="text-blue-600 hover:text-blue-800 p-1" title="View Document">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={() => downloadFile(doc.url, doc.fileName)} className="text-green-600 hover:text-green-800 p-1" title="Download Document">
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p className="truncate">{doc.fileName}</p>
                        <p>Size: {(doc.size / 1024).toFixed(1)} KB</p>
                        <p>Uploaded: {formatDate(doc.uploadedAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderStatusTimeline = () => {
    const items = timelineEvents || [];
    const getIconForType = (type) => {
      const mapping = {
        submission: FileText,
        medical_review: Stethoscope,
        policy_review: Shield,
        financial_review: CreditCard,
        kyc_verification: User,
        status_change: Activity,
        dispute_raised: AlertTriangle,
        acknowledged: Activity,
        under_review: Activity,
        dispute_resolved: CheckCircle,
        doc_query_open: Paperclip,
        doc_query_resolved: CheckCircle,
      };
      return mapping[type] || Activity;
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Application Status Timeline
          </h2>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
              application.status
            )}`}
          >
            {application.status?.replace("_", " ").charAt(0).toUpperCase() +
              application.status?.replace("_", " ").slice(1)}
          </span>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-6">
            {items.length === 0 && (
              <div className="text-center text-sm text-gray-500">No timeline events yet.</div>
            )}
            {items.map((item, index) => {
              const Icon = getIconForType(item.type);
              const isCompleted = !!item.completed;
              const isActive = !isCompleted && index === items.findIndex((s) => !s.completed);

              return (
                <div key={`${item.id || item.type}-${index}`} className="relative">
                  {index < items.length - 1 && (
                    <div
                      className={`absolute left-4 top-8 w-0.5 h-12 ${
                        isCompleted ? "bg-green-300" : "bg-gray-200"
                      }`}
                    />
                  )}

                  <div className="flex items-start space-x-4">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                        isCompleted
                          ? "bg-green-100 border-green-500"
                          : isActive
                          ? "bg-yellow-100 border-yellow-500"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          isCompleted
                            ? "text-green-600"
                            : isActive
                            ? "text-yellow-600"
                            : "text-gray-400"
                        }`}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3
                          className={`font-medium ${
                            isCompleted
                              ? "text-gray-900"
                              : isActive
                              ? "text-gray-900"
                              : "text-gray-500"
                          }`}
                        >
                          {item.title}
                        </h3>
                        {item.timestamp && (
                          <span className="text-sm text-gray-500">
                            {formatDate(item.timestamp)}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p
                          className={`text-sm ${
                            isCompleted
                              ? "text-gray-600"
                              : isActive
                              ? "text-gray-600"
                              : "text-gray-400"
                          }`}
                        >
                          {item.description}
                        </p>
                      )}

                      {item.actor?.name && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 border text-gray-700">
                            {item.actor.name}{item.actor.team ? ` • ${item.actor.team}` : ''}
                          </span>
                        </div>
                      )}

                      {Array.isArray(item.attachments) && item.attachments.length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          {item.attachments.map((att, i) => (
                            <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                              <Paperclip className="h-3 w-3" /> {att.name || 'Attachment'}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Show policy verification details */}
                      {item.type === "policy_review" &&
                        application.workflow?.policyVerification?.comments && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                            <p className="text-sm text-purple-800">
                              {application.workflow.policyVerification.comments}
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderEnhancedPolicyReview = () => (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" style={{height: 'calc(100vh - 200px)'}}>
      {/* Left Side - Policy Review Form (60%) */}
      <div className="lg:col-span-3 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pr-2" style={{maxHeight: 'calc(100vh - 200px)'}}>
        <div className="space-y-6">
          
          {/* Treatment Details Indicator */}
          {(() => {
            const mv = application?.workflow?.medicalVerification || {};
            const statusRaw = String(mv.status || '').toLowerCase();
            const approvedSignals = [
              statusRaw === 'approved',
              mv?.approvedForProcess === true,
              application?.status === 'medical_approved',
              application?.status === 'approved_for_process',
              application?.status === 'approved',
              Boolean(mv?.verifiedAt),
              (application?.medical?.approval?.status || '').toLowerCase() === 'approved',
              application?.medical?.doctorApproval === true,
              (application?.workflow?.doctorReview?.status || '').toLowerCase() === 'approved'
            ];
            const rejectedSignals = [
              statusRaw === 'rejected',
              (application?.medical?.approval?.status || '').toLowerCase() === 'rejected'
            ];
            const diagnosisApproved = approvedSignals.some(Boolean) && !rejectedSignals.some(Boolean);
            const treatments = Array.isArray(application?.medical?.treatments) ? application.medical.treatments : [];
            const primary = treatments[0] || {};
            const diagnosis = primary.diagnosisName || application?.medical?.diagnosis || 'Diagnosis not specified';
            const icdCode = primary.icdCode || application?.medical?.icdCode || null;
            const icdDescription = primary.icdDescription || application?.medical?.icdDescription || null;
            const type = primary.treatmentType || application?.medical?.treatmentType || null;
            const procedure = primary.procedureName || application?.medical?.procedureDetails || null;
            const reviewerName = primary.reviewerName || application?.medical?.doctor?.name || null;
            const reviewedAt = primary.reviewedAt || application?.medical?.lastReviewedAt || null;
            const statusText = diagnosisApproved
              ? 'approved'
              : (statusRaw || (application?.medical?.approval?.status || 'pending'));
            const lowered = String(statusText).toLowerCase();
            const statusBadge = diagnosisApproved
              ? 'bg-green-50 text-green-700 border border-green-200'
              : (lowered.includes('pending') || lowered.includes('review'))
                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-gray-50 text-gray-700 border border-gray-200';
            const statusLabel = `Diagnosis ${String(statusText).replace('_',' ')}`;
            return (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-4 flex items-start justify-between">
                  <div className="flex items-start space-x-3 min-w-0">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Stethoscope className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">Treatment Details</h3>
                      <div className="mt-1 text-sm text-gray-700 truncate">
                        {diagnosis}
                      </div>
                      <div className="mt-1 text-xs text-gray-600 space-x-2 flex flex-wrap">
                        {icdCode && (
                          <span>
                            <span className="font-medium">ICD:</span> {icdCode}{icdDescription ? ` - ${icdDescription}` : ''}
                          </span>
                        )}
                        {type && (
                          <span className="before:content-['•'] before:mx-2">
                            <span className="font-medium">Type:</span> {type}
                          </span>
                        )}
                        {procedure && (
                          <span className="before:content-['•'] before:mx-2 truncate">
                            <span className="font-medium">Procedure:</span> {procedure}
                          </span>
                        )}
                      </div>
                      {(reviewerName || reviewedAt) && (
                        <div className="mt-2 text-xs text-gray-500">
                          Last reviewed{reviewerName ? ` by ${reviewerName}` : ''}{reviewedAt ? ` on ${formatDate(reviewedAt)}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusBadge}`}>
                    {statusLabel.replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                </div>
              </div>
            );
          })()}
          
          {/* Draft/Approval Status Banner */}
          {(savedDataAvailable || isFormInViewMode) && (
            <div className={`rounded-lg border p-4 ${
              isFormInViewMode 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start space-x-3">
                <div className={`p-1 rounded-full ${
                  isFormInViewMode ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {isFormInViewMode ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${
                    isFormInViewMode ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {isFormInViewMode 
                      ? 'Policy Verification Completed' 
                      : 'Draft Data Available'
                    }
                  </h4>
                  <p className={`text-sm mt-1 ${
                    isFormInViewMode ? 'text-green-700' : 'text-blue-700'
                  }`}>
                    {isFormInViewMode 
                      ? 'This policy verification has been completed and approved. You are viewing the submitted data in read-only mode.'
                      : `You have saved draft data from ${lastSavedAt?.toLocaleDateString()} at ${lastSavedAt?.toLocaleTimeString()}. You can continue editing your verification.`
                    }
                  </p>
                  {!isFormInViewMode && (
                    <div className="mt-2 flex space-x-3">
                      <button
                        onClick={() => {
                          localStorage.removeItem(`policy_review_${applicationId}`);
                          setAutoSaveData(null);
                          setLastSavedAt(null);
                          setSavedDataAvailable(false);
                          window.location.reload();
                        }}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        Clear Draft & Start Fresh
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* Policy Snapshot */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="bg-green-100 p-2 rounded-lg">
                <Shield className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">Policy Snapshot</h3>
            </div>
            {!isFormInViewMode && (
              <div className="flex items-center space-x-2">
                {snapshotEditing ? (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          setSnapshotSaving(true);
                          const res = await updatePolicyInformation(
                            applicationId,
                            {
                              policyNumber: snapshotForm.policyNumber,
                              insuranceCompany: snapshotForm.insuranceCompany,
                              policyType: snapshotForm.policyType,
                              tpaName: snapshotForm.tpaName
                            },
                            {}
                          );
                          if (!res?.success) throw new Error(res?.error || 'Failed to save');
                          setSnapshotEditing(false);
                          await loadApplicationData();
                          const toast = document.createElement('div');
                          toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
                          toast.textContent = 'Policy details saved';
                          document.body.appendChild(toast);
                          setTimeout(() => { document.body.removeChild(toast); }, 1500);
                        } catch (e) {
                          const toast = document.createElement('div');
                          toast.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
                          toast.textContent = 'Failed to save policy details';
                          document.body.appendChild(toast);
                          setTimeout(() => { document.body.removeChild(toast); }, 1800);
                        } finally {
                          setSnapshotSaving(false);
                        }
                      }}
                      disabled={snapshotSaving}
                      className="px-2 py-1 bg-teal-600 text-white rounded-lg text-xs hover:bg-teal-700 disabled:opacity-50 flex items-center space-x-1"
                    >
                      <Save className="h-3 w-3" />
                      <span>{snapshotSaving ? 'Saving...' : 'Save'}</span>
                    </button>
                    <button
                      onClick={() => {
                        setSnapshotEditing(false);
                        setSnapshotForm({
                          policyNumber: application.policy?.number || '',
                          insuranceCompany: application.policy?.insuranceCompany || '',
                          policyType: application.policy?.type || '',
                          tpaName: application.policy?.tpaName || application.policy?.tpa || ''
                        });
                      }}
                      className="px-2 py-1 border border-gray-300 text-gray-700 rounded-lg text-xs hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setSnapshotEditing(true)}
                    className="px-2 py-1 border border-gray-300 text-gray-700 rounded-lg text-xs hover:bg-gray-50 flex items-center space-x-1"
                  >
                    <Edit className="h-3 w-3" />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Policy Number */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <FileText className="h-3 w-3 text-gray-400" /> Policy Number
              </label>
              {snapshotEditing ? (
                <input
                  type="text"
                  value={snapshotForm.policyNumber}
                  onChange={(e) => setSnapshotForm(s => ({ ...s, policyNumber: e.target.value }))}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter policy number"
                />
              ) : (
                <p className="text-sm font-mono text-gray-900 mt-0.5 truncate">{application.policy?.number || '—'}</p>
              )}
            </div>

            {/* Insurance Company */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <Building className="h-3 w-3 text-gray-400" /> Insurance Company
              </label>
              {snapshotEditing ? (
                <select
                  value={snapshotForm.insuranceCompany}
                  onChange={(e) => setSnapshotForm(s => ({ ...s, insuranceCompany: e.target.value }))}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select Insurance Company</option>
                  {[
                    "Bajaj Allianz General Insurance Co. Ltd",
                    "Cholamandalam MS General Insurance Co. Ltd",
                    "Navi General Insurance Ltd.",
                    "Future Generali India Insurance Co. Ltd.",
                    "HDFC ERGO General Insurance Co. Ltd",
                    "IFFCO-TOKIO General Insurance Co. Ltd.",
                    "ICICI Lombard General Insurance Co. Ltd",
                    "Kotak Mahindra General insurance co. Ltd.",
                    "Liberty General Insurance Co. Ltd.",
                    "Magma HDI General Insurance Co. Ltd.",
                    "National Insurance Co. Ltd.",
                    "Raheja QBE General Insurance Co. Ltd.",
                    "Reliance General Insurance Co. Ltd.",
                    "Royal Sundaram General Insurance Co. Ltd",
                    "SBI General Insurance Co. Ltd.",
                    "Shriram General Insurance Co. Ltd.",
                    "Tata-AIG General Insurance Co. Ltd.",
                    "The New India Assurance Co. Ltd.",
                    "The Oriental Insurance Co. Ltd.",
                    "Universal Sompo General Insurance Co. Ltd.",
                    "United India Insurance Co. Ltd.",
                    "Acko General Insurance Ltd.",
                    "Edelweiss General Insurance Company Limited",
                    "Go Digit General Insurance Limited",
                    "Aditya Birla Health insurance Co Ltd.",
                    "Manipal Cigna Health Insurance Company Limited",
                    "Niva bupa health insurance company limited",
                    "Care Health Insurance Ltd",
                    "Star Health & Allied Insurance Co. Ltd",
                  ].map((company) => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-900 mt-0.5 truncate">{application.policy?.insuranceCompany || '—'}</p>
              )}
            </div>

            {/* Policy Type */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <Shield className="h-3 w-3 text-gray-400" /> Policy Type
              </label>
              {snapshotEditing ? (
                <select
                  value={snapshotForm.policyType}
                  onChange={(e) => setSnapshotForm(s => ({ ...s, policyType: e.target.value }))}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select Policy Type</option>
                  <option value="Retail">Retail</option>
                  <option value="corporate">Corporate</option>
              
                </select>
              ) : (
                <span className={`inline-flex mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                  application.policy?.type === 'corporate' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {(application.policy?.type || '—').toString().charAt(0).toUpperCase() + (application.policy?.type || '—').toString().slice(1)}
                </span>
              )}
            </div>

            {/* TPA */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <label className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <Building className="h-3 w-3 text-gray-400" /> TPA
              </label>
              {snapshotEditing ? (
                <select
                  value={snapshotForm.tpaName}
                  onChange={(e) => setSnapshotForm(s => ({ ...s, tpaName: e.target.value }))}
                  className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select TPA</option>
                  {[
                    "In-House",
                    "Medi Assist Insurance TPA Private Limited",
                    "MDIndia Health Insurance TPA Private Limited",
                    "Paramount Health Services & Insurance TPA Private Limited",
                    "Heritage Health Insurance TPA Private Limited",
                    "Family Health Plan Insurance TPA Limited",
                    "Raksha Health Insurance TPA Private Limited",
                    "Vidal Health Insurance TPA Private Limited",
                    "Volo Health Insurance TPA Pvt. Ltd ( Formerly East West Assist Insurance TPA Private Limited)",
                    "Medsave Health Insurance TPA Limited",
                    "Genins India Insurance TPA Limited",
                    "Health India Insurance TPA Services Private Limited",
                    "Good Health Insurance TPA Limited",
                    "Park Mediclaim Insurance TPA Private Limited",
                    "Safeway Insurance TPA Private Limited",
                    "Ericson Insurance TPA Private Limited",
                    "Health Insurance TPA of India Limited",
                    "Vision Digital Insurance TPA Private Limited",
                    "Link-K Insurance TPA Private Limited",
                    "AKNA Health Insurance TPA Private Limited",
                  ].map((tpa) => (
                    <option key={tpa} value={tpa}>{tpa}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-900 mt-0.5 truncate">{application.policy?.tpaName || application.policy?.tpa || '—'}</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Policy Verification
              </h2>
            </div>
            {lastSavedAt && !isFormInViewMode && (
              <div className="text-sm text-gray-500">
                Auto-saved {lastSavedAt.toLocaleTimeString()}
              </div>
            )}
          </div>

          <PolicyVerificationForm
            application={application}
            onSubmit={handlePolicyVerificationSubmit}
            onClose={() => setActiveTab("details")}
            autoSaveData={autoSaveData}
            onAutoSave={saveToLocalStorage}
            onRequestInfo={handleRequestInfo}
            hasPendingClarification={hasPendingClarification}
            clarificationDetails={clarificationDetails}
          />
        </div>
        </div>
      </div>

      {/* Right Side - Application Overview & Documents (40%) */}
      <div className="lg:col-span-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 pl-2" style={{maxHeight: 'calc(100vh - 200px)'}}>
        <ApplicationRightPanel
          // Document panel props
          showDocumentPanel={showDocumentPanel}
          selectedDocument={selectedDocument}
          onCloseDocumentPanel={closeDocumentPanel}
          onOpenDocument={openDocument}
          
          // Discussion panel props
          showDiscussionPanel={showDiscussionPanel}
          onCloseDiscussionPanel={() => setShowDiscussionPanel(false)}
          onOpenDiscussionPanel={() => setShowDiscussionPanel(true)}
          applicationId={applicationId}
          onUnreadCountChange={handleUnreadCountChange}
          hasPendingClarification={hasPendingClarification}
          unreadCount={unreadCount}
          
          // Application data
          application={application}
          
          // Collapsible sections
          collapsedSections={collapsedSections}
          onToggleSection={toggleSection}
          
          // Utility functions
          formatAmount={formatAmount}
          formatDate={formatDate}
          showPolicyEdit={true}
          onPolicyUpdated={loadApplicationData}

          // Document query props
          documentQueries={documentQueries}
          onOpenQueryModal={openQueryModal}
          userTeam="policy"
        />
      </div>
    </div>
  );

  // Render disputes management
  const renderDisputes = () => (
    <div className="p-6">
      <DisputeManager 
        applicationId={applicationId}
        applicationData={application}
        onDisputeUpdate={handleDisputeUpdate}
        forceDepartment="policy"
      />
    </div>
  );

  const renderSanctions = () => {
    return (
      <div className="p-6">
        <SanctionLettersSection application={application} sanctions={sanctions} formatDate={formatDate} />
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "details":
        return renderApplicationDetails();
      case "documents":
        return renderDocuments();
      case "policy-review":
        return renderEnhancedPolicyReview();
      case "enach":
        return renderENachTab();
      case "consultations":
        return (
          <ConsultationScheduler 
            application={application}
            onConsultationUpdate={loadApplicationData}
          />
        );
      case "discussions":
        return (
          <DiscussionComponent
            applicationId={applicationId}
            onUnreadCountChange={handleUnreadCountChange}
          />
        );
      case "disputes":
        return renderDisputes();
      case "sanctions":
        return renderSanctions();
      case "status":
        return renderStatusTimeline();
      default:
        return renderApplicationDetails();
    }
  };

  const renderENachTab = () => {
    const en = application?.workflow?.enach || application?.workflow?.eNach || {};
    const isInitiated = en?.status === 'initiated';
    const isCompleted = en?.status === 'completed';

    const handleInitiate = async () => {
      setEnachErr(''); setEnachMsg(''); setEnachBusy(true);
      try {
        const { submitENach } = await import('../../services/financialservice');
        const res = await submitENach(applicationId, user.uid || 'policy-user', {
          link: (enachForm.link || '').trim(),
          comments: (enachForm.comments || '').trim()
        });
        if (!res?.success) throw new Error(res?.error || 'Failed to initiate');
        setEnachMsg('eNACH initiated');
        await loadApplicationData();
      } catch (e) {
        setEnachErr(e?.message || 'Failed to initiate');
      } finally {
        setEnachBusy(false);
      }
    };

    const handleMarkDone = async () => {
      setEnachErr(''); setEnachMsg('');
      if (!enachFile) { setEnachErr('Please upload the eNACH done screenshot (PNG/JPG/PDF)'); return; }
      setEnachBusy(true);
      try {
        const { markENachCompleted } = await import('../../services/financialservice');
        const res = await markENachCompleted(applicationId, user.uid || 'policy-user', enachFile);
        if (!res?.success) throw new Error(res?.error || 'Failed to mark done');
        setEnachMsg('eNACH marked completed');
        setEnachFile(null);
        await loadApplicationData();
      } catch (e) {
        setEnachErr(e?.message || 'Failed to mark completed');
      } finally {
        setEnachBusy(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="bg-teal-100 p-2 rounded-lg"><CreditCard className="h-5 w-5 text-teal-600"/></div>
              <h2 className="text-lg font-semibold text-gray-900">eNACH</h2>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full border ${isCompleted ? 'bg-green-50 text-green-700 border-green-200' : isInitiated ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
              {isCompleted ? 'Completed' : isInitiated ? 'Initiated' : 'Not Started'}
            </span>
          </div>

          {/* Existing info */}
          {(en?.link || en?.comments) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {en?.link && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Link</label>
                  <div className="text-sm"><a href={en.link} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{en.link}</a></div>
                </div>
              )}
              {en?.comments && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Comments</label>
                  <div className="text-sm text-gray-800">{en.comments}</div>
                </div>
              )}
            </div>
          )}

          {/* Timestamps & proof details */}
          {(isInitiated || isCompleted) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {en?.submittedAt && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Initiated On</label>
                  <div className="text-sm text-gray-800">{formatDate(en.submittedAt)}</div>
                </div>
              )}
              {isCompleted && en?.hospitalCompletedAt && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Completed On</label>
                  <div className="text-sm text-gray-800">{formatDate(en.hospitalCompletedAt)}</div>
                </div>
              )}
              {isCompleted && en?.hospitalProof && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Proof Uploaded</label>
                  <div className="text-sm text-gray-800">
                    <a href={en.hospitalProof.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">
                      {en.hospitalProof.name || 'Download proof'}
                    </a>
                    {en?.hospitalProof?.uploadedAt && (
                      <div className="text-xs text-gray-500 mt-0.5">Uploaded on {formatDate(en.hospitalProof.uploadedAt)}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isInitiated && !isCompleted && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">eNACH Link (optional)</label>
                  <input type="url" value={enachForm.link} onChange={(e)=>setEnachForm(s=>({...s, link:e.target.value}))} placeholder="https://mandate-url.example/..." className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Comments (optional)</label>
                  <input type="text" value={enachForm.comments} onChange={(e)=>setEnachForm(s=>({...s, comments:e.target.value}))} placeholder="Any note for hospital" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"/>
                </div>
              </div>
              {enachErr && <p className="text-xs text-red-600">{enachErr}</p>}
              {enachMsg && <p className="text-xs text-green-700">{enachMsg}</p>}
              <button onClick={handleInitiate} disabled={enachBusy} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-60 text-sm font-medium">{enachBusy ? 'Initiating…' : 'Create / Initiate eNACH'}</button>
            </div>
          )}

          {isInitiated && !isCompleted && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Upload eNACH Done Screenshot (required)</label>
              <input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e)=>setEnachFile(e.target.files && e.target.files[0])} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"/>
              {enachErr && <p className="text-xs text-red-600 mt-1">{enachErr}</p>}
              {enachMsg && <p className="text-xs text-green-700 mt-1">{enachMsg}</p>}
              <button onClick={handleMarkDone} disabled={enachBusy} className="mt-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-60 text-sm font-medium">{enachBusy ? 'Saving…' : 'Mark eNACH Done'}</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Application Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The requested application could not be found.
          </p>
          <button
            onClick={() => onBack ? onBack() : navigate(-1)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 relative">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto w-[95%] px-2">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onBack ? onBack() : navigate(-1)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Policy Review
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>ID: {application.applicationId}</span>
                  <span>•</span>
                  <span>Patient: {application.patient?.name}</span>
                  <span>•</span>
                  <span>Hospital: {application.hospitalName || application.hospital?.name}</span>
                  <span>•</span>
                  <span>Submitted: {formatDate(application.submittedAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Global Search Bar */}
              <div className="w-80 hidden lg:block">
                <GlobalSearchBar userRole="policy" />
              </div>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                  application.status
                )}`}
              >
                {application.status?.replace("_", " ").charAt(0).toUpperCase() +
                  application.status?.replace("_", " ").slice(1)}
              </span>

              {application.priority && application.priority !== "normal" && (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    application.priority === "urgent"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {application.priority.charAt(0).toUpperCase() +
                    application.priority.slice(1)}
                </span>
              )}

              {application.emergency && (
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                  Emergency
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto w-[95%] px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? "border-teal-500 text-teal-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.count !== null && tab.count > 0 && (
                    <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="mx-auto w-[95%] px-4 sm:px-6 lg:px-4 py-8">
        {renderTabContent()}
      </div>

      {/* Full-height Document Viewer for Documents tab */}
      {activeTab === 'documents' && showDocumentPanel && selectedDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={closeDocumentPanel} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-[92vw] mx-auto border border-gray-200">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className="min-w-0 pr-3">
                <h3 className="text-lg font-semibold text-gray-900 truncate">Document Viewer</h3>
                <p className="text-sm text-gray-600 truncate">{selectedDocument.fileName}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => downloadFile(selectedDocument.url, selectedDocument.fileName)}
                  className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center space-x-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                <button onClick={closeDocumentPanel} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="w-full h-[80vh] border border-gray-200 rounded overflow-hidden bg-gray-50">
                {selectedDocument.fileName?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={selectedDocument.url}
                    className="w-full h-full"
                    title="Document Viewer"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <img
                      src={selectedDocument.url}
                      alt={selectedDocument.fileName}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Query Modal */}
      {showQueryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Raise Document Query</h3>
                    <p className="text-sm text-gray-600">
                      {queryDocumentKey?.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowQueryModal(false);
                    setQueryComment("");
                    setQueryDocumentKey("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(80vh-180px)]">
              {/* Previous queries */}
              {documentQueries[queryDocumentKey] && documentQueries[queryDocumentKey].length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900">Previous Queries</h4>
                  {documentQueries[queryDocumentKey].map((query) => (
                    <div key={query.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{query.raisedBy?.name}</span>
                            <span className="text-xs text-gray-500">({query.raisedBy?.team})</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              query.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {query.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{query.comment}</p>
                        </div>
                      </div>
                      {query.replies && query.replies.length > 0 && (
                        <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                          {query.replies.map((reply, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-medium text-gray-900">{reply.userName}:</span>{" "}
                              <span className="text-gray-700">{reply.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* New query input */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">New Query</h4>
                <textarea
                  value={queryComment}
                  onChange={(e) => setQueryComment(e.target.value)}
                  placeholder="Describe the issue with this document..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQueryModal(false);
                  setQueryComment("");
                  setQueryDocumentKey("");
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRaiseQuery}
                disabled={!queryComment.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Raise Query
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PolicyApplicationDetails;
